import React, { memo } from "react";
import WhiteboardCanvas from "./whiteboard/WhiteboardCanvas";
import ModernToolbar from "./whiteboard/ModernToolbar";
import { EmptyStateWithTemplates } from "./whiteboard/EmptyStateWithTemplates";
import useProductionFlowState from "@/hooks/useProductionFlowState";
import CloudSyncBanner from "./CloudSyncBanner";

import ErrorBoundary from "./ErrorBoundary";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import "@/styles/node-shapes.css";
import "@/styles/node-drag-optimizations.css";

const Whiteboard: React.FC = memo(() => {
  console.log("Whiteboard component rendering...");
  
  const { preferences } = useUserPreferences();
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onNodeClick,
    onEdgeClick,
    colorPickerValue,
    selectedCategory,
    selectedShape,
    handleColorChange,
    handleCategoryChange,
    handleShapeChange,
    addNewNode,
    currentWhiteboardId,
    currentWhiteboardName,
    handleChangeWhiteboardName,
    isCollaborationEnabled,
    toggleCollaboration,
    collaborators,
    handleChangeAllNodesColor,
    isLoading,
    isSaving,
    handleSaveWhiteboard,
    handleLoadWhiteboard,
    handleCreateNewWhiteboard,
    userWhiteboards,
    canUndo,
    canRedo,
    undo,
    redo
  } = useProductionFlowState();

  // Temporary keyboard shortcuts implementation to avoid hook context issues
  React.useEffect(() => {
    if (!preferences.keyboard_shortcuts_enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.contentEditable === 'true') {
        return;
      }

      const isCtrl = event.ctrlKey || event.metaKey;
      if (isCtrl) {
        switch (event.key.toLowerCase()) {
          case 'z':
            if (event.shiftKey && redo) {
              event.preventDefault();
              redo();
            } else if (undo) {
              event.preventDefault();
              undo();
            }
            break;
          case 'y':
            if (redo) {
              event.preventDefault();
              redo();
            }
            break;
          case 's':
            if (handleSaveWhiteboard) {
              event.preventDefault();
              handleSaveWhiteboard();
            }
            break;
          case 'n':
            if (handleCreateNewWhiteboard) {
              event.preventDefault();
              handleCreateNewWhiteboard();
            }
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, handleSaveWhiteboard, handleCreateNewWhiteboard, preferences.keyboard_shortcuts_enabled]);

  console.log("Whiteboard state:", {
    nodeCount: nodes?.length || 0,
    edgeCount: edges?.length || 0,
    selectedCategory,
    selectedShape,
    currentWhiteboardName,
    isCollaborationEnabled,
    isLoading,
    isSaving,
    canUndo,
    canRedo
  });

  if (isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading whiteboard...</p>
        </div>
      </div>
    );
  }

  // Show empty state if no nodes exist
  const isEmpty = !nodes || nodes.length === 0;

  return (
    <ErrorBoundary fallback={
      <div className="h-full w-full flex items-center justify-center">
        <div className="text-destructive">Whiteboard error. Please refresh.</div>
      </div>
    }>
      <div className={`h-full w-full flex flex-col overflow-hidden ${preferences.theme === 'dark' ? 'dark' : ''}`}>
        {/* Modern Toolbar */}
        <div className="flex-shrink-0">
          <ModernToolbar
            onAddNode={addNewNode}
            selectedCategory={selectedCategory}
            selectedShape={selectedShape}
            onCategoryChange={handleCategoryChange}
            onShapeChange={handleShapeChange}
            colorPickerValue={colorPickerValue}
            onColorChange={handleColorChange}
            onChangeAllNodesColor={handleChangeAllNodesColor}
            nodes={nodes || []}
            edges={edges || []}
            whiteboardName={currentWhiteboardName || "Untitled Whiteboard"}
          />
        </div>
        
        {/* Cloud sync banner (shown when signed out) */}
        <div className="flex-shrink-0">
          <CloudSyncBanner />
        </div>
        
        {/* Main Content Area */}
        <div className="flex-1 overflow-hidden">
          {isEmpty ? (
            <EmptyStateWithTemplates
              onCreateBlank={addNewNode}
              onSelectTemplate={(template) => {
                // Handle template selection
                console.log("Selected template:", template);
                addNewNode();
              }}
            />
          ) : (
            <WhiteboardCanvas
              nodes={nodes || []}
              edges={edges || []}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              onEdgeClick={onEdgeClick}
            />
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
});

Whiteboard.displayName = 'Whiteboard';

export default Whiteboard;
